import { SmartTable } from '@toggle/design-system';
import { Property } from 'csstype';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddWatchlistTriggerButton } from '~/components/watchlist/add-watchlist-trigger-button/AddWatchlistTriggerButton';
import { WatchlistTrigger } from '~/components/watchlist/watchlist-trigger/WatchlistTrigger';

import * as S from './AssetScreeningWidget.styles';

type AssetScreeningColumns = {
  key: string;
};

export interface AssetScreeningWidgetProps<T> {
  columnsData: AssetScreeningColumns[];
  data: T[];
}

export const PAGINATION_RANGE = 10;

export const chunkArray = <T,>(arr: T[], size: number): T[][] =>
  arr.length > size
    ? [arr.slice(0, size), ...chunkArray(arr.slice(size), size)]
    : [arr];

export const AssetScreeningWidget = <T extends {}>({
  columnsData,
  data,
}: AssetScreeningWidgetProps<T>) => {
  const { t } = useTranslation(['widget', 'snakes', 'analyze']);
  const chunkedData = chunkArray(data, PAGINATION_RANGE);
  const [activeIndex, setActiveIndex] = useState(0);

  const mapColumns = () => {
    const leftAlignment = 'left' as Property.TextAlign;
    return columnsData.map(c => {
      if (c.key === 'ticker') {
        return {
          label: t('widget:assetScreeningWidget.ticker'),
          key: 'ticker',
          disableHiding: true,
          alignment: leftAlignment,
          renderHeader: () => (
            <S.TickerColumnHeader>
              {t('widget:assetScreeningWidget.ticker')}
            </S.TickerColumnHeader>
          ),
          render: (value: string, item: any) => (
            <S.TickerColumnContent>
              <WatchlistTrigger
                tag={item.tag}
                dropdownProps={{
                  strategy: 'fixed',
                  placement: 'bottom-start',
                }}
                renderTrigger={isInWatchlist => (
                  <AddWatchlistTriggerButton isInWatchlist={isInWatchlist} />
                )}
              />
              {item.logo_url ? (
                <S.TickerColumnImg src={item.logo_url} alt="" />
              ) : (
                <S.LogoPlaceHolder>{value[0].toUpperCase()}</S.LogoPlaceHolder>
              )}

              <span>{value.toUpperCase()}</span>
            </S.TickerColumnContent>
          ),
        };
      } else {
        return {
          /* i18next-extract-disable-next-line */
          label: t(`snakes:labels.${c.key}`),
          key: c.key,
        };
      }
    });
  };

  const columns = mapColumns();

  return (
    <>
      <S.Root>
        <SmartTable
          isHeaderSticky
          columns={columns}
          data={chunkedData[activeIndex]}
          alignment="right"
        />
      </S.Root>
      {data.length > PAGINATION_RANGE && (
        <S.StyledPagination
          pages={chunkedData.map((_, i) => `${i + 1}`)}
          activeIndex={activeIndex}
          onClick={setActiveIndex}
        />
      )}
    </>
  );
};
